import { TFunction } from "i18next";
import * as Yup from "yup";

import { AttendanceEntryRequest, DayOfWeek } from "@/api";
import {
  AttendanceEntryFormValues,
  WeeklyPlanningDayFormValues,
  WeeklyPlanningFormValues,
} from "@/common/components/appointments/plan-attendance/PlanAttendanceDialog.types";
import {
  attendanceEntrySchema,
  dateToDayOfWeek,
} from "@/common/components/appointments/plan-attendance/PlanAttendanceDialog.utils";
import { deriveAvailability } from "@/common/components/appointments/shared/AppointmentForm.utils";
import { SystemDropdownOptions } from "@/common/hooks/useSystemDropdownOptions";

function buildDayEntries(
  weekday: DayOfWeek,
  dayEntries: AttendanceEntryFormValues[],
  purposes: SystemDropdownOptions
): AttendanceEntryRequest[] {
  const result: AttendanceEntryRequest[] = [];

  for (const entry of dayEntries) {
    if (!entry.purposeKey || entry.attendanceType === null || !entry.startTime || !entry.endTime) {
      continue;
    }

    result.push({
      weekday,
      purposeKey: entry.purposeKey,
      attendanceType: entry.attendanceType,
      availability: deriveAvailability(entry.purposeKey, purposes),
      startTime: entry.startTime,
      endTime: entry.endTime,
      details: entry.note || null,
      substituteCounselorId: entry.substituteCounselorId,
    });
  }

  return result;
}

export function buildWeeklyPlanningEntries(
  days: WeeklyPlanningDayFormValues[],
  purposes: SystemDropdownOptions
): AttendanceEntryRequest[] {
  return days.flatMap((day) => buildDayEntries(dateToDayOfWeek(day.date), day.entries, purposes));
}

function weeklyPlanningDaySchema(t: TFunction): Yup.ObjectSchema<WeeklyPlanningDayFormValues> {
  return Yup.object({
    date: Yup.date().required(),
    entries: Yup.array().of(attendanceEntrySchema(t)).required() as Yup.Schema<AttendanceEntryFormValues[]>,
  });
}

export function weeklyPlanningValidationSchema(t: TFunction): Yup.ObjectSchema<WeeklyPlanningFormValues> {
  return Yup.object({
    days: Yup.array().of(weeklyPlanningDaySchema(t)).required() as Yup.Schema<WeeklyPlanningDayFormValues[]>,
  });
}
